/**
 * Master list of vendors and their products for service health filtering.
 * This is the canonical list - not dynamically generated from incidents.
 */

export interface VendorDefinition {
  id: string;
  name: string;
  icon: string;
  color: string;
  products: string[];
}

// Map old vendor IDs to new consolidated ones
export const vendorIdMap: Record<string, string> = {
  'microsoft365': 'microsoft',
  'azure': 'microsoft',
  'datto': 'datto',
  'itglue': 'datto',  // ITGlue is a Datto product
  'connectwise': 'connectwise',
};

// Master vendor definitions with all known products
export const vendors: VendorDefinition[] = [
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: 'MS',
    color: '#0078d4',
    products: [
      // Microsoft 365 Services
      'Exchange Online',
      'Microsoft Teams',
      'SharePoint Online',
      'OneDrive for Business',
      'Microsoft 365 Apps',
      'Outlook',
      'Yammer',
      'Planner',
      'Power Apps',
      'Power Automate',
      'Power BI',
      'Microsoft Forms',
      'Microsoft Stream',
      'Microsoft Bookings',
      'Microsoft To Do',
      'Microsoft Defender',
      'Microsoft Intune',
      // Azure Services
      'Azure Active Directory',
      'Azure DevOps',
      'Azure Portal',
      'Azure Storage',
      'Azure Virtual Machines',
      'Azure SQL Database',
      'Azure Functions',
      'Azure App Service',
      'Azure Kubernetes Service',
      'Azure Backup',
      'Azure Site Recovery',
      'Azure Monitor',
      'Azure Key Vault',
    ],
  },
  {
    id: 'datto',
    name: 'Datto / Kaseya',
    icon: 'DT',
    color: '#199ed9',
    products: [
      // Datto Products
      'SaaS Protection',
      'BCDR',
      'SIRIS',
      'ALTO',
      'Datto Networking',
      'Datto RMM',
      'Autotask PSA',
      // Kaseya Products
      'VSA 10',
      'VSA X',
      'BMS',
      'IT Complete',
      // IT Glue
      'IT Glue',
      'IT Glue Network Glue',
      'MyGlue',
    ],
  },
  {
    id: 'connectwise',
    name: 'ConnectWise',
    icon: 'CW',
    color: '#e31937',
    products: [
      'ConnectWise Manage',
      'ConnectWise Automate',
      'ConnectWise ScreenConnect',
      'ConnectWise Control',
      'ConnectWise Sell',
      'ConnectWise CPQ',
      'ConnectWise BrightGauge',
      'ConnectWise Command',
      'ConnectWise SIEM',
      'ConnectWise MDR',
      'ConnectWise Home',
      'ConnectWise Identify',
    ],
  },
];

// Get vendor definition by ID
export function getVendor(id: string): VendorDefinition | undefined {
  const mappedId = vendorIdMap[id] || id;
  return vendors.find(v => v.id === mappedId);
}

// Get all vendor IDs
export function getVendorIds(): string[] {
  return vendors.map(v => v.id);
}

// Normalize a vendor:product key (handles old vendor IDs)
export function normalizeProductKey(vendor: string, product: string): string {
  const mappedVendor = vendorIdMap[vendor] || vendor;
  return `${mappedVendor}:${product}`;
}
